import { useState } from "react";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { Field, PasswordInput } from "@/components/auth/Field";
import { PasswordPolicyHint, usePasswordPolicy } from "@/components/auth/PasswordPolicyHint";
import { Spinner } from "@/components/common/FullScreenLoader";
import { SectionHeading } from "@/components/shell/StatCard";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export function ChangePasswordForm({ onChanged }) {
  const { user, refresh } = useAuth();
  const policy = usePasswordPolicy();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== next;

  async function submit(event) {
    event.preventDefault();
    if (mismatch) return;
    setError(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/password/change", { current_password: current, new_password: next });
      toast.success(data.revoked_sessions ? `Password changed · ${data.revoked_sessions} other session(s) signed out` : "Password changed");
      setCurrent("");
      setNext("");
      setConfirm("");
      await refresh();
      onChanged?.();
    } catch (e) {
      const err = apiError(e);
      setError(err.errors?.length ? err.errors.join(" ") : err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="bz-card">
      <SectionHeading title="Change password" description="Changing your password signs out every other session." />
      <form data-testid="security-change-password-form" onSubmit={submit} className="grid gap-5 lg:grid-cols-2" noValidate>
        <div className="space-y-5">
          <Field id="current-password" label="Current password">
            <PasswordInput id="current-password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} testId="security-current-password-input" />
          </Field>
          <Field id="next-password" label="New password">
            <PasswordInput id="next-password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} testId="security-new-password-input" />
          </Field>
          <Field id="confirm-next-password" label="Confirm new password" error={mismatch ? "Passwords do not match." : null} testId="security-confirm-password">
            <PasswordInput id="confirm-next-password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} invalid={mismatch} testId="security-confirm-password-input" />
          </Field>
        </div>
        <div className="space-y-5">
          <PasswordPolicyHint password={next} email={user.email} policy={policy} />
          {error ? (
            <div role="alert" data-testid="security-change-password-error" className="bz-alert-error">
              {error}
            </div>
          ) : null}
          <button type="submit" disabled={busy || !current || !next || !confirm || mismatch} data-testid="security-change-password-submit" className="bz-btn-primary w-full lg:w-auto">
            {busy ? <Spinner /> : <KeyRound className="h-4 w-4" strokeWidth={2} />}
            Update password
          </button>
        </div>
      </form>
    </section>
  );
}
